/**
 * Shared leave-display helpers.
 *
 * Single source of truth for resolving leave type names, OIL credited days,
 * and cross-type consumption detection from a leave application object.
 * Used by admin Applications tab, admin Balances history modal, and employee Leave page.
 */

import { formatLeaveDays, pluralizeDays } from './numberUtils';
import { formatDate, formatDaySpan, getCalendarDaysUntil } from './dateUtils';

/**
 * Calendar-day parents store the trip length on the consumption proposal
 * and may keep a different working-day figure on ``leave_total_days``.
 * Mirrors ``HolidayService.CALENDAR_DAY_LEAVE_TYPES`` (OIL claims excluded —
 * those surfaces read credited days, not this helper).
 */
const CALENDAR_DAY_LEAVE_TYPES = new Set([
  'Home Leave',
  'Urgent Leave',
]);

export const isCalendarDayLeaveType = (leaveTypeName) => (
  CALENDAR_DAY_LEAVE_TYPES.has((leaveTypeName || '').trim())
);

export const getAppliedLeaveTypeName = (application) => (
  application?.leave_type?.leave_type_name
  || application?.leave_type_id?.leave_type_name
  || application?.leave_type_name
  || application?.leave_type
  || ''
).trim();

/**
 * Headline day count for a leave application.
 *
 * Home Leave / Urgent Leave: prefer ``leave_consumption_proposal.total_days``
 * so a 2.5 AL + 27.5 HL trip reads as 30 calendar days, not the working-day
 * remainder on ``leave_total_days``.
 *
 * Every other type: prefer ``leave_total_days``. That is the entitlement
 * figure (Saturday AL = 1.0 on a 5.5-day week). A locked proposal can lag
 * that figure by 0.5 when an old AM-trim treated Saturday as a half day —
 * using the proposal as the headline made the detail drawer disagree with
 * the applications list (S0166 / LA#4395, 9–12 Sep 2026).
 */
export const getDisplayTotalDays = (application, fallback = 0) => {
  if (isCalendarDayLeaveType(getAppliedLeaveTypeName(application))) {
    const proposalTotal = application?.leave_consumption_proposal?.total_days;
    if (proposalTotal != null && proposalTotal !== '') {
      const n = Number(proposalTotal);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  const raw = Number(
    application?.leave_total_days
    ?? application?.total_days
    ?? fallback
  );
  if (Number.isFinite(raw) && raw > 0) return raw;
  const fb = Number(fallback);
  return Number.isFinite(fb) ? fb : 0;
};

/**
 * AM/PM chips that describe a half-day *deduction*, not the physical
 * Saturday morning shift.
 *
 * A 5.5-day Saturday is already only a morning. Annual Leave still
 * consumes 1.0 day for that shift, so an "AM" chip next to a whole-day
 * total reads as 3.5 when the bucket is 4. Only show boundary chips when
 * the headline duration is actually fractional (or a same-day 0.5).
 */
export const getLeavePeriodDisplayChips = (application) => {
  const provided = application?.leave_display_period_chips || {};
  const startDate = application?.leave_start_date || application?.start_date;
  const endDate = application?.leave_end_date || application?.end_date;
  const isSameDay = !startDate || !endDate || String(startDate) === String(endDate);
  const days = getDisplayTotalDays(application);
  const isHalfDay = Number(days) === 0.5;
  const isFractional = Number.isFinite(Number(days)) && Number(days) % 1 !== 0;

  if (isSameDay) {
    return {
      start: isHalfDay
        ? (provided.start || application?.leave_start_time_period || null)
        : null,
      end: null,
    };
  }

  if (!isFractional) {
    return { start: null, end: null };
  }

  return {
    start: provided.start
      || (application?.leave_start_time_period === 'PM' ? 'PM' : null),
    end: provided.end
      || (application?.leave_end_time_period === 'AM' ? 'AM' : null),
  };
};

export const isSickLeaveTypeName = (name) => {
  const n = (name || '').toLowerCase();
  return (n.includes('outpatient') && n.includes('sick')) || n.includes('hospitalisation');
};

export const getDisplayLeaveTypeName = (application) => {
  const applied = getAppliedLeaveTypeName(application);

  // Sick leave (OSL/HL) is ALWAYS shown as the sick-leave type it is — a
  // verified medical certificate underlies it. When the paid balance is
  // exhausted the days are consumed as Unpaid Leave, but that is a PAY
  // treatment surfaced separately (consumption breakdown + status indicator),
  // NOT a change of leave type. Flipping the badge to "Unpaid Leave" would
  // erase the fact that this was a genuine, MC-verified sick leave.
  if (isSickLeaveTypeName(applied)) return applied;

  const proposalItems = application?.leave_consumption_proposal?.items;
  if (!Array.isArray(proposalItems) || proposalItems.length !== 1) return applied;

  const consumedType = (proposalItems[0]?.leave_type || '').trim();
  if (!consumedType || consumedType === applied) return applied;

  const consumedDays = Number(proposalItems[0]?.days || 0);
  const proposalTotal = Number(
    application?.leave_consumption_proposal?.total_days
    ?? application?.leave_total_days
    ?? consumedDays
  );
  const fullyCovered = consumedDays > 0 && Math.abs(consumedDays - proposalTotal) < 0.001;
  return fullyCovered ? consumedType : applied;
};

/**
 * Extract the Unpaid Leave portion of a leave's consumption proposal.
 *
 * Used to flag when a leave (typically an OSL/HL whose paid balance is
 * exhausted) is actually paid out as Unpaid Leave. Returns null when there is
 * no unpaid portion.
 *
 * @returns {null | {unpaidDays:number, paidDays:number, totalDays:number, isFullyUnpaid:boolean}}
 */
export const getUnpaidConsumption = (application) => {
  const items = application?.leave_consumption_proposal?.items;
  if (!Array.isArray(items) || items.length === 0) return null;

  const unpaidDays = items
    .filter((i) => (i?.leave_type || '').trim() === 'Unpaid Leave')
    .reduce((sum, i) => sum + (parseFloat(i?.days) || 0), 0);
  if (unpaidDays <= 0) return null;

  const totalDays = Number(
    application?.leave_consumption_proposal?.total_days
    ?? application?.leave_total_days
    ?? unpaidDays
  );
  const paidDays = Math.max(0, totalDays - unpaidDays);
  return {
    unpaidDays,
    paidDays,
    totalDays,
    isFullyUnpaid: paidDays < 0.001,
  };
};

export const getOilCreditedDays = (application) => {
  const leaveTypeName = getAppliedLeaveTypeName(application);
  if (leaveTypeName !== 'Claim: Off in Lieu') return null;

  if (Array.isArray(application?.oil_dates) && application.oil_dates.length > 0) {
    return application.oil_dates.reduce((sum, entry) => sum + (parseFloat(entry?.days) || 0), 0);
  }
  if (application?.oil_credited_days != null && application.oil_credited_days !== '') {
    return Number(application.oil_credited_days);
  }
  return null;
};

/**
 * Status-aware verb for OIL claim days in list/detail UIs.
 *
 * - approved / auto_approved → already banked ("credited")
 * - rejected / cancelled → no verb (claim amount only; nothing banked)
 * - pending / submitted / verified / other → not banked yet ("to credit")
 *
 * @param {string} [status]
 * @returns {'credited' | 'to credit' | null}
 */
export const getOilCreditPhrase = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'approved' || s === 'auto_approved') return 'credited';
  if (s === 'rejected' || s === 'cancelled') return null;
  return 'to credit';
};

/**
 * FIFO OIL expiry line on employee leave cards.
 * Quantity at risk stays in days; distance to the expiry date uses formatDaySpan.
 *
 * @param {object} balance
 * @returns {null|{text:string, isExpiringSoon:boolean}}
 */
export const buildOilExpiryHint = (balance) => {
  const earliestDateStr = balance?.earliest_oil_expiry_date || balance?.earliest_expiry_date;
  if (!earliestDateStr) return null;

  const daysUntilExpiry = getCalendarDaysUntil(earliestDateStr);
  if (daysUntilExpiry == null) return null;

  const isExpiringSoon = daysUntilExpiry <= 14;
  const formattedExpiry = formatDate(earliestDateStr);
  const entries = balance.oil_expiry_entries || [];
  const daysAtEarliestExpiry = entries
    .filter((e) => e.expiry_date === earliestDateStr)
    .reduce((sum, e) => sum + (e.remaining_days || 0), 0);
  const totalOilDays = entries.reduce((sum, e) => sum + (e.remaining_days || 0), 0);
  const hasMultipleExpiries = entries.length > 1 && daysAtEarliestExpiry < totalOilDays;
  const atRiskQty = hasMultipleExpiries ? `${formatLeaveDays(daysAtEarliestExpiry)}d` : '';

  const text = isExpiringSoon
    ? `${atRiskQty ? `${atRiskQty} — ` : ''}Use by ${formattedExpiry} (${formatDaySpan(daysUntilExpiry, 'compact')} left)`
    : `${atRiskQty ? `${atRiskQty} expires` : 'Expires'} ${formattedExpiry}`;

  return { text, isExpiringSoon };
};

/**
 * Full OIL claim days label, e.g. "+0.5 day credited" / "+0.5 day to credit" / "+0.5 day".
 *
 * @param {number|string} days
 * @param {string} [status]
 * @returns {string}
 */
export const formatOilCreditDaysLabel = (days, status) => {
  const label = `+${formatLeaveDays(days)} ${pluralizeDays(days)}`;
  const phrase = getOilCreditPhrase(status);
  return phrase ? `${label} ${phrase}` : label;
};

/**
 * Tailwind text classes for OIL claim days by status (teal while active/approved; muted when dead).
 *
 * @param {string} [status]
 * @returns {string}
 */
export const getOilCreditDaysClassName = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'rejected' || s === 'cancelled') return 'text-gray-500 font-medium';
  return 'text-teal-700 font-medium';
};

/**
 * Returns true when the consumption proposal contains leave types that differ
 * from the badge display type (e.g. OIL + AL split for an Annual Leave request).
 */
export const hasCrossTypeConsumption = (application, displayTypeName) => {
  if (getAppliedLeaveTypeName(application) === 'Claim: Off in Lieu') return false;
  const items = application?.leave_consumption_proposal?.items;
  if (!Array.isArray(items) || items.length === 0) return false;
  return items.length > 1 ||
    (items.length === 1 && (items[0]?.leave_type || '').trim() !== displayTypeName);
};

/**
 * Plain-language notes for an Annual-Leave-first Home / Urgent Leave split
 * (``extension_preview`` response), so a worker can see why only part of the
 * trip comes out of Annual Leave.
 *
 * @param {object} preview
 * @returns {string[]} Sentences, most important first.
 */
export const getOverseasLeaveSplitNotes = (preview) => {
  if (!preview) return [];

  const calendarDays = Number(preview.calendar_days) || 0;
  // Employees see balances floored to 0.5 (formatLeaveDaysEmployee), and AL
  // is drawn in the same half-day steps — speak in that unit only.
  const usableBalance = Math.floor(Math.max(0, Number(preview.annual_leave_balance) || 0) * 2) / 2;
  const annualDays = Number(preview.annual_leave_days) || 0;
  const remainderDays = Number(preview.remainder_days) || 0;
  const remainderType = preview.remainder_leave_type || 'Home Leave';
  const isSingular = (n) => Math.abs(n) <= 1;
  const asDays = (n) => `${formatLeaveDays(n)} ${isSingular(n) ? 'day' : 'days'}`;

  if (remainderDays <= 0) {
    return [`Fully covered by your Annual Leave (${asDays(annualDays)}).`];
  }

  if (annualDays <= 0) {
    return [`You have no Annual Leave left, so all ${asDays(calendarDays)} ${isSingular(calendarDays) ? 'is' : 'are'} ${remainderType}.`];
  }

  const usesAllAnnualLeave = annualDays >= usableBalance;
  const notes = [
    `Your Annual Leave is used first: ${asDays(annualDays)}${usesAllAnnualLeave ? ' (all you have left)' : ''}. `
    + `The other ${asDays(remainderDays)} ${isSingular(remainderDays) ? 'is' : 'are'} ${remainderType}.`,
  ];
  if (!usesAllAnnualLeave) {
    notes.push(`Rest days and public holidays in this period count as ${remainderType}, not Annual Leave.`);
  }
  return notes;
};

const LEAVE_TYPE_ABBREVIATIONS = {
  'Off in Lieu': 'OIL',
  'Marriage Leave': 'ML',
  'Annual Leave': 'AL',
  'Unpaid Leave': 'UL',
  'Home Leave': 'HL',
  'Outpatient Sick Leave': 'OSL',
  'Hospitalisation Leave': 'HSL',
};

/**
 * Official abbreviations for leave types whose *full compound name* overflows
 * a table-fixed Leave Type column. Matches configuration LeaveType.abbreviation
 * seeds (CL(IF), CL(GP), CCL(NC), ML(CC), …).
 *
 * Do not invent page-local shortenings. Do not ellipsis-truncate the subtype —
 * Immediate Family vs Grandparent is the 3-day vs 1-day entitlement.
 */
const DENSE_TABLE_LEAVE_TYPE_ABBREVIATIONS = {
  'Compassionate Leave (Immediate Family)': 'CL(IF)',
  'Compassionate Leave (Grandparent)': 'CL(GP)',
  'Childcare Leave (Non-Citizen)': 'CCL(NC)',
  'Maternity Leave (Citizen Child)': 'ML(CC)',
  'Maternity Leave (Non-Citizen Child)': 'ML(NC)',
  'Maternity Leave (Non-Citizen Child - Eligible)': 'ML(NCE)',
  'Maternity Leave (Unpaid)': 'ML(U)',
};

export const toCanonicalLeaveTypeName = (leaveTypeName) => {
  const trimmed = (leaveTypeName || '').trim();
  const paren = /^(.+?)\s+\(([^)]+)\)$/.exec(trimmed);
  if (paren) return `${paren[1].trim()} (${paren[2].trim()})`;
  const sep = /^(.+?)\s+[·–—-]\s+(.+)$/.exec(trimmed);
  if (sep) return `${sep[1].trim()} (${sep[2].trim()})`;
  return trimmed;
};

export const getDenseLeaveTypeAbbreviation = (leaveTypeName) => {
  const canonical = toCanonicalLeaveTypeName(leaveTypeName);
  return DENSE_TABLE_LEAVE_TYPE_ABBREVIATIONS[canonical] || null;
};

export const getLeaveTypeAbbreviation = (leaveType) =>
  LEAVE_TYPE_ABBREVIATIONS[leaveType] || leaveType;

export const formatConsumptionProposalText = (items) => {
  if (!Array.isArray(items)) return '';
  const visibleItems = items.filter((item) => Number(item?.days || 0) > 0);
  const balanceYears = new Set(
    visibleItems
      .map((item) => Number(item?.balance_year))
      .filter(Number.isFinite)
  );
  const showBalanceYear = balanceYears.size > 1;

  return visibleItems
    .map((item) => {
      const yearLabel = showBalanceYear && item.balance_year
        ? ` ${item.balance_year}`
        : '';
      return `${formatLeaveDays(item.days)} ${getLeaveTypeAbbreviation(item.leave_type)}${yearLabel}`;
    })
    .join(' + ');
};

/**
 * Resolve EA / OIL project fields from a leave application payload.
 * Prefers activity_* (External Activity) then oil_* (OIL claim).
 *
 * @param {object} application
 * @returns {{ code: string, name: string }}
 */
export const getLeaveProjectFields = (application) => {
  const code = (
    application?.activity_project_code
    || application?.oil_project_code
    || ''
  ).trim();
  const name = (
    application?.activity_project_name
    || application?.oil_project_name
    || ''
  ).trim();
  return { code, name };
};

/**
 * Calculate the next accrual date for earn-as-you-work leave types.
 *
 * Full-time: accrual falls on the employee's monthly work anniversary
 * ("monthsary") — the same calendar day as their employment start date.
 * Part-time: accrual is the 1st of each calendar month (aligned with payroll).
 *
 * Handles month-end overflow (e.g. start day 31 → last day of shorter months)
 * and Feb 29 leap-year edge cases, mirroring the backend is_monthsary_today().
 */
export const getNextAccrualDate = (employmentStartDate, isPartTime) => {
  if (!employmentStartDate) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (isPartTime) {
    return new Date(today.getFullYear(), today.getMonth() + 1, 1);
  }

  const startDay = new Date(employmentStartDate).getDate();

  const monthsaryDay = (year, month) => {
    const lastDay = new Date(year, month + 1, 0).getDate();
    return Math.min(startDay, lastDay);
  };

  const thisMonthDay = monthsaryDay(today.getFullYear(), today.getMonth());
  if (thisMonthDay > today.getDate()) {
    return new Date(today.getFullYear(), today.getMonth(), thisMonthDay);
  }

  const nextMonth = today.getMonth() + 1;
  const nextYear = today.getFullYear() + (nextMonth > 11 ? 1 : 0);
  const nextMonthNorm = nextMonth % 12;
  return new Date(nextYear, nextMonthNorm, monthsaryDay(nextYear, nextMonthNorm));
};

export const formatAccrualDate = (date) => {
  if (!date) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
